<template>
  <div class="animate-fade-in-right">
    <!-- Header Controls -->
    <header
      class="mb-8 flex flex-col md:flex-row justify-between items-start md:items-end gap-6"
    >
      <div>
        <div class="flex items-center gap-4 mb-2">
          <button
            @click="$router.push({ name: 'dashboard' })"
            class="bg-white/10 hover:bg-white/20 p-2 rounded-xl text-white transition-colors backdrop-blur-md border border-white/10"
          >
            <svg
              class="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>
          <h2
            class="text-4xl font-bold text-white tracking-tight drop-shadow-sm"
          >
            {{ t('All Events') }}
          </h2>
        </div>
        <p class="text-white/70 ml-14 font-medium">
          {{ t('Manage and organize your wedding events.') }}
        </p>
      </div>

      <button
        @click="handlePopOpenform('add')"
        class="group relative overflow-hidden bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-400 hover:to-purple-500 text-white font-bold py-3 px-8 rounded-2xl shadow-[0_4px_20px_rgba(99,102,241,0.4)] transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_8px_30px_rgba(99,102,241,0.5)]"
      >
        <span class="flex items-center gap-2 relative z-10">
          <svg
            class="w-5 h-5"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 4v16m8-8H4"
            />
          </svg>
          {{ t('Add New Event') }}
        </span>
      </button>
    </header>

    <!-- Filters -->
    <div class="mb-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      <!-- Search Input -->
      <div class="relative group">
        <div
          class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
        >
          <svg
            class="w-5 h-5 text-indigo-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
        <input
          v-model="searchQuery"
          type="text"
          :placeholder="t('Search items...')"
          class="w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-indigo-200/50 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-white/10 transition-all font-medium"
        />
      </div>

      <!-- Date Filter -->
      <div class="relative group">
        <div
          class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none z-10"
        >
          <svg
            class="w-5 h-5 text-indigo-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
        </div>
        <DatePicker
          v-model="dateFilter"
          class="w-full"
          dateFormat="dd-M-yy"
          :placeholder="t('Select Date')"
          fluid
          inputId="dd"
        />
      </div>

      <!-- Status Filter (Added for extra "class") -->
      <div class="relative group hidden lg:block">
        <div
          class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none"
        >
          <svg
            class="w-5 h-5 text-indigo-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
            />
          </svg>
        </div>
        <select
          v-model="statusFilter"
          class="w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white/80 rounded-2xl py-4 pl-12 pr-4 focus:outline-none focus:ring-2 focus:ring-indigo-500/50 focus:bg-white/10 transition-all font-medium cursor-pointer appearance-none"
        >
          <option class="bg-slate-800" value="all">{{ t('All Statuses') }}</option>
          <option class="bg-slate-800" value="Completed">{{ t('Completed') }}</option>
          <option class="bg-slate-800" value="Pending">{{ t('Pending') }}</option>
        </select>
        <div
          class="absolute inset-y-0 right-0 pr-4 flex items-center pointer-events-none"
        >
          <svg
            class="w-4 h-4 text-white/50"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>
    </div>

    <div
      v-if="filteredEvents.length > 0"
      class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
    >
      <div
        v-for="event in filteredEvents"
        :key="event.id"
       
        class="group  bg-white/10 backdrop-blur-md rounded-3xl border border-white/20 p-6 transition-all duration-300 hover:bg-white/20 hover:-translate-y-2 hover:shadow-[0_20px_40px_rgba(0,0,0,0.2)]"
      >
        <div class="flex justify-between items-start mb-6">
          <div
            class="p-3 rounded-2xl bg-gradient-to-br from-indigo-500/20 to-purple-500/20 border border-white/10 group-hover:border-white/30 transition-colors"
          >
            <span class="text-3xl">🎉</span>
          </div>

          <div class="flex items-center gap-2">
            <!-- Edit Button -->
            <button
              @click.stop="handlePopOpenform('edit', event)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-white/20 text-indigo-300 hover:text-white transition-all border border-transparent hover:border-white/20"
              :title="t('Edit Event')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                />
              </svg>
            </button>

            <!-- Delete Button -->
            <button
              @click.stop="handlePopDelete(event)"
              class="w-8 h-8 flex items-center justify-center rounded-full bg-white/5 hover:bg-red-500/20 text-red-300 hover:text-red-200 transition-all border border-transparent hover:border-red-500/30"
              :title="t('Delete Event')"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="w-4 h-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
            </button>

            <!-- Status Badge -->
            <div
              @click.stop="handlePopOpenform('editStatus', event)"
              class="ml-2 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider border cursor-pointer"
              :class="
                event.statusString
                  ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/30'
                  : 'bg-amber-500/20 text-amber-300 border-amber-500/30'
              "
            >
              {{ event.statusString ? t(event.statusString) : t('Pending') }}
            </div>
          </div>
        </div>

        <h3
          class="text-2xl font-bold text-white mb-2 group-hover:text-indigo-200 transition-colors"
        >
          {{ event.eventName }}
        </h3>
        <div class="flex flex-col gap-2 text-white/60 text-sm mb-6">
          <div class="flex items-center gap-2">
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            {{ formatDate(event.eventStartDate) }} -
            {{ formatDate(event.eventEndDate) }}
          </div>
          <!-- <div class="flex items-center gap-2">
            <svg
              class="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
              />
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
              />
            </svg>
            {{ event.location || "Location Pending" }}
          </div> -->
        </div>

        <div
          class="flex items-center justify-between border-t border-white/10 pt-4"
        >
          <div class="flex flex-col gap-2">
            <div
              @click.stop="
                $router.push({ name: 'event-guests', params: { id: event.id } })
              "
              class="text-indigo-300 text-sm font-bold hover:translate-x-1 transition-transform cursor-pointer"
            >
              {{ t('Manage Guests ->') }}
            </div>
            <div
              @click.stop="
                $router.push({
                  name: 'event-expenses',
                  params: { id: event.id },
                })
              "
              class="text-amber-400 text-sm font-bold hover:translate-x-1 transition-transform cursor-pointer"
            >
              {{ t('Manage Expenses ->') }}
            </div>
          </div>
          <div class="flex  -space-x-2 self-end">
            <!-- Mock avatars -->
            <div
              class="w-8 h-8 rounded-full bg-white/20 border-2 border-transparent flex items-center justify-center text-[10px] text-white font-bold"
            >
              +{{ event.guests?.length }}
            </div>
            <!-- expese -->
            <div
              class="w-8 h-8 rounded-full bg-white/20 border-2 border-transparent flex items-center justify-center text-[10px] text-white font-bold"
            >
              +{{ event.expenses?.length }}
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Empty State -->
    <div
      v-else
      class="flex flex-col items-center justify-center py-20 text-white/50"
    >
      <svg
        class="w-20 h-20 mb-4 opacity-30"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          stroke-linecap="round"
          stroke-linejoin="round"
          stroke-width="2"
          d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
        />
      </svg>
      <p class="text-xl font-medium">{{ t('No events found matching your filters.') }}</p>
      <button
        @click="resetFilters"
        class="mt-4 text-indigo-300 hover:text-white underline transition-colors"
      >
        {{ t('Clear all filters') }}
      </button>
    </div>
  </div>

  <component
    :is="currentComponent"
    :visible="openForm"
    :isEditDoc="isEditDoc"
    :doc="docData"
    :isFailed="isFailed"
    :elementName="elementName"
    :docId="deleteId"
    :elementCategory="elementCategory"
    :informMessage="informMessage"
    :showToast="showToast"
    :isLastRecordOnPage="isLastRecordOnPage"
    @onClose="handleListenEmitEvent"
  />
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import ToastedVue from "@/components/ToastedVue.vue";
import EventForm from "@/components/Modal/EventForm.vue";
import { projectAuth } from "@/firebase/config";

import { where, orderBy, documentId } from "firebase/firestore";

import { formatDate } from "@/composables/formatDate";
import DeleteConfirmation from "@/components/DeleteConfirmation.vue";
import getRelationShipCollection from "@/composables/getRelationShipCollection";
import checkExistData from "@/composables/checkExist";
import StatusUpdateModal from "@/components/StatusUpdateModal.vue";
import { getCollectionQuery } from "@/composables/getCollection";
import { calculateExpirePlan } from "@/composables/calculateExpirePlan";
import { useI18n } from "vue-i18n";

const { t } = useI18n();

const searchQuery = ref("");
const dateFilter = ref(null);
const openForm = ref(false);
const isEditDoc = ref(false);
const informMessage = ref("");
const showToast = ref(false);
const isFailed = ref(false);
const currentComponent = ref("");
const statusFilter = ref("all");

const docData = ref(null);
const elementCategory = ref(null);
const elementName = ref(null);
const isLoading = ref(false);
const deleteId = ref(null);
const isLastRecordOnPage = ref(false);
const events = ref([]);
const isExpired = ref(false);
const userId=ref(projectAuth.currentUser?.uid)

const handlePopOpenform = (status, item) => {
  if (status == "add") {
    if (planByID.value[0]?.maxEvent === events.value.length) {
      isFailed.value = true;
      informMessage.value = t("You have reached the maximum number of events allowed in your plan!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    } 
    else if(isExpired.value){
      isFailed.value = true;
      informMessage.value = t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    else {
      openForm.value = true;
      currentComponent.value = EventForm;
    }
  } else if (status == "editStatus") {
    if(isExpired.value){
      isFailed.value = true;
      informMessage.value = t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    docData.value = item;
    deleteId.value = item.id;
    openForm.value = true;
    currentComponent.value = StatusUpdateModal;
  } else {
    if (isExpired.value) {
      isFailed.value = true;
      informMessage.value = t("Your subscription has expired!");
      showToast.value = true;
      currentComponent.value = ToastedVue;
      return;
    }
    isEditDoc.value = true;
    docData.value = item;
    openForm.value = true;
    currentComponent.value = EventForm;
  }
};

const handlePopDelete = async (item) => {
  const check = await checkExistData(
    [
      {
        collection: "guests",
        field: `eventId`,
      },
      {
        collection: "expenses",
        field: `eventId`,
      },
    ],
    item.id
  );

  if (check) {
    isFailed.value = true;
    informMessage.value = t("Can not delete event must delete guests or expense first!");
    showToast.value = true;
    currentComponent.value = ToastedVue;
    return;
  }

  if(isExpired.value){
    isFailed.value = true;
    informMessage.value = t("Your subscription has expired!");
    showToast.value = true;
    currentComponent.value = ToastedVue;
    return;
  }
  deleteId.value = item.id;
  elementCategory.value = "events";
  elementName.value = item.eventName;
  showToast.value = true;
  currentComponent.value = DeleteConfirmation;
};

const userByID = ref([]);
const fetchUserById = async () => {
  try {
    await getCollectionQuery(
      "users",
      [where(documentId(), "==", userId.value)],
      (data) => {
        userByID.value = data;
        if(userByID.value.length > 0){
          fetchPlanById();
        }
       
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

const planByID = ref([]);
const fetchPlanById = async () => {
  try {
    await getCollectionQuery(
      "plans",
      [where(documentId(), "==", userByID.value[0]?.planId)],
      (data) => {
        planByID.value = data;
      },
      null,
      true
    );
  } catch (error) {
    console.log("failed to fetch event", error);
  }
};

watch(
  [userByID, planByID],
  ([newUser, newPlan]) => {
    if (newUser?.length > 0 && newPlan?.length > 0) {
      const user = newUser[0];
      const plan = newPlan[0];

      if (
        user.subscriptionStart &&
        plan.planDuration &&
        plan.planType
      ) {
        const res = calculateExpirePlan(
          user.subscriptionEnd,
          plan.planType
        );
        isExpired.value = res;

   
      } else {
        console.log("Waiting for subscription data...", {
          hasStart: !!user?.subscriptionStart,
          hasDuration: !!plan?.planDuration,
          hasType: !!plan?.planType
        });
      }
    }
  },
  {
    immediate: true,
    deep: true,
  }
);

const { mainDocuments, fetchRelatedDocuments } = getRelationShipCollection({
  guests: { collection: "guests", after: "guests", field: "eventId" },
  expenses: { collection: "expenses", after: "expenses", field: "eventId" },
});

onMounted(async () => {
   fetchUserById();
  
  fetchRelatedDocuments("events", {
    whereDoc: [
      where("status", "==", true),
      where("userId", "==", projectAuth.currentUser.uid),
    ],
    orderByDoc: ["createdAt", "desc"],
  });
});
watch(mainDocuments, (newDocs) => {
  events.value = newDocs;
});

const handleListenEmitEvent = (status) => {
  if (status == "add" || status == "create") {
    informMessage.value = t("Data has been created successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;

    currentComponent.value = ToastedVue;
    showToast.value = true;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  } else if (status == "update") {
    informMessage.value = t("Data has been updated successfully !");
    searchQuery.value = "";

    currentComponent.value = ToastedVue;
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  } else if (status == "close") {
    console.log("part close");
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  } else if (status == "delete") {
    informMessage.value = t("Data has been deleted successfully !");

    showToast.value = true;
    currentComponent.value = ToastedVue;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  } else if (status == "copy") {
    informMessage.value = t("Data has been copied successfully !");
    showToast.value = true;
    isFailed.value = false;
    openForm.value = false;
    currentComponent.value = "";
    isEditDoc.value = false;
    docData.value = null;
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  } else {
    showToast.value = false;
    isFailed.value = false;
    openForm.value = false;
    isEditDoc.value = false;
    docData.value = null;
    currentComponent.value = "";
    elementCategory.value = null;
    elementName.value = null;
    isLastRecordOnPage.value = null;
  }
};

const resetFilters = () => {
  searchQuery.value = "";
  dateFilter.value = "";
};

const filteredEvents = computed(() => {
  return events.value.filter((event) => {
    // 1. Name Filter
    const matchesName = event.eventName
      .toLowerCase()
      .includes(searchQuery.value.toLowerCase());

    // 2. Status Filter
    let matchesStatus = true;
    if (statusFilter.value !== "all") {
      matchesStatus = event.statusString === statusFilter.value;
    }

    let matchesDate = true;
    if (dateFilter.value) {
      const eventDate = new Date(event.eventStartDate).toDateString();
      const eventEndDate = new Date(event.eventEndDate).toDateString();
      const filterDate = new Date(dateFilter.value).toDateString(); // May need timezone adjustment in real app

      matchesDate = eventDate === filterDate || eventEndDate === filterDate;
    }

    return matchesName && matchesDate && matchesStatus;
  });
});
</script>

<style scoped>
.animate-fade-in-right {
  animation: fadeInRight 0.5s ease-out forwards;
}

@keyframes fadeInRight {
  from {
    opacity: 0;
    transform: translateX(20px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
}

:deep(.p-datepicker) {
  @apply rounded-2xl border border-white/10 bg-slate-900/90 backdrop-blur-xl shadow-xl;
}

:deep(.p-datepicker-input) {
  @apply w-full bg-white/5 backdrop-blur-xl border border-white/20 text-white placeholder-indigo-200/50 rounded-2xl py-4 pl-12 pr-4 transition-all font-medium;
}

:deep(.p-datepicker-input:focus) {
  @apply outline-none ring-2 ring-indigo-500/50 bg-white/10;
}

/* Calendar Panel Styles */
:deep(.p-datepicker-header) {
  @apply border-b border-white/10 bg-transparent text-white pt-4 pb-2;
}

:deep(.p-datepicker-title) {
  @apply text-white font-bold;
}

:deep(.p-datepicker-prev-icon),
:deep(.p-datepicker-next-icon) {
  @apply text-white/70 hover:text-white transition-colors;
}

:deep(.p-datepicker-table) {
  @apply my-2;
}

:deep(.p-datepicker-table th) {
  @apply text-indigo-300 font-medium p-2;
}

:deep(.p-datepicker-table td) {
  @apply p-1;
}

:deep(.p-datepicker-table td > span) {
  @apply text-white/90 w-8 h-8 rounded-full transition-all border border-transparent;
}

:deep(.p-datepicker-table td > span:hover) {
  @apply bg-white/10 text-white;
}

:deep(.p-datepicker-table td.p-datepicker-today > span) {
  @apply bg-indigo-500/20 text-indigo-300 border-indigo-500/50 font-bold;
}

:deep(.p-datepicker-table td > span.p-highlight) {
  @apply bg-indigo-600 text-white shadow-lg shadow-indigo-600/20;
}

:deep(.p-button-text) {
  @apply text-indigo-300 hover:bg-indigo-500/10 hover:text-indigo-200;
}
</style>
